import { Injectable } from '@angular/core';
import {
    catchError,
    combineLatest,
    distinctUntilChanged,
    map,
    Observable,
    of,
    switchMap,
    take,
    throwError,
} from 'rxjs';
import { TimeBudget, TimeBudgetErrorCode, TimeBudgetUpdateRequestDto } from '@/core/models/time-budget';
import { UserPermission } from '@/core/models/user-permission';
import { TimeBudgetRepository } from '@/core/repositories/time-budget/time-budget.repository';
import { UserService } from '@/core/services/open-api/user.service';
import { TypeGuard } from '@/core/types/type-guard';

/**
 * @class TimeBudgetService
 * @description Time budgets on tiles and the time tracked against them, which only the backend counts.
 */
@Injectable({ providedIn: 'root' })
export class TimeBudgetService {
    /** Whether the current user may see time budgets. False while the account's switch is off. */
    public readonly canRead$: Observable<boolean>;

    constructor(
        private readonly timeBudgetRepository: TimeBudgetRepository,
        private readonly userService: UserService,
    ) {
        this.canRead$ = combineLatest([this.userService.account$, this.userService.currentUser$]).pipe(
            map(([account, user]) => {
                const enabled = account.accountSettings?.timeBudgetsEnabled === true;
                // regular users only see budgets when an admin allows it
                const visibleToUser =
                    user.hasPermission(UserPermission.ACTIVITY_READ_TEAM) ||
                    (account.accountSettings?.timeBudgetsVisibleToAllUsers === true &&
                        user.hasPermission(UserPermission.TILE_READ));
                return enabled && visibleToUser;
            }),
            distinctUntilChanged(),
        );
    }

    /**
     * Fetches the time budgets (switched on and off) with their usage rows.
     * @param tileIds Only budgets on these tiles. Leave out for all budgets of the account.
     */
    public list(tileIds?: number[]): Observable<TimeBudget[]> {
        if (tileIds !== undefined && tileIds.length === 0) {
            return of([]); // the API would treat an empty filter as "all budgets"
        }
        // the tiles page loads the budgets on its own, so nothing is requested while the API would refuse it
        return this.canRead$.pipe(
            take(1),
            switchMap((canRead) =>
                canRead
                    ? this.timeBudgetRepository
                          .list(tileIds)
                          .pipe(catchError((error: unknown) => this.onRequestError(error)))
                    : of([]),
            ),
        );
    }

    public create(tileId: number, budgetSeconds: number): Observable<TimeBudget> {
        return this.timeBudgetRepository
            .create({ tileId, budgetSeconds })
            .pipe(catchError((error: unknown) => this.onRequestError(error)));
    }

    public update(timeBudgetId: number, changes: TimeBudgetUpdateRequestDto): Observable<TimeBudget> {
        return this.timeBudgetRepository
            .update(timeBudgetId, changes)
            .pipe(catchError((error: unknown) => this.onRequestError(error)));
    }

    /** Returns the TBG-* code of a failed time budget request, if there is one. */
    public static getErrorCode(error: unknown): TimeBudgetErrorCode | undefined {
        if (!TypeGuard.isHttpApiError(error)) {
            return undefined;
        }
        const code = error.error.error.code;
        return Object.values(TimeBudgetErrorCode).find((value) => value === code);
    }

    private onRequestError(error: unknown): Observable<never> {
        const code = TimeBudgetService.getErrorCode(error);
        if (code === TimeBudgetErrorCode.Disabled || code === TimeBudgetErrorCode.NotVisibleToAllUsers) {
            // a setting was turned off since the account was loaded: refresh it so that the budgets disappear
            this.userService.loadAccount(true).subscribe({ error: () => {} });
        }
        return throwError(() => error);
    }
}
